import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, deleteDataAPI, postDataAPI } from '../../utils/fetchData';
import { canDeleteContent, canAccessFinancials } from '../../utils/permissions';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import { OnboardingActionButton } from '../../components/Onboarding/OnboardingActionButton';
import AddToCalendarButton from '../../components/Connectors/AddToCalendarButton';
import {
  getCurrentPhase,
  checkUserData,
  transitionToPhase,
  ONBOARDING_PHASES
} from '../../utils/onboardingStateMachine';
import { getOnboardingDialogue } from '../../utils/interactiveTour';
import ApprovalModal from '../../components/ApprovalModal';
import { useApproval } from '../../hooks/useApproval';
import { canEditApp } from '../../utils/permissions';
import Bandsintown from '../../svg/EventPlatform/Bandsintown';

const ViewAll = () => {
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();
    const { approvalModal, openApprovalModal, closeApprovalModal, sendBookingForApproval } = useApproval();
    const [activeTab, setActiveTab] = useState('confirmed');
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showImportDropdown, setShowImportDropdown] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const token = localStorage.getItem('dizeeToken');
    const hasFinancialAccess = canAccessFinancials('bookings');

    useEffect(() => {
        loadBookings();
    }, []);

    const loadBookings = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            const response = await getDataAPI('user/getBookings', token);
            
            if (response.data.status === 200 && response.data.data) {
                const bookingsData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                if (Array.isArray(bookingsData)) {
                    setBookings(bookingsData);
                } else {
                    setBookings([]);
                }
            } else {
                setBookings([]);
            }
        } catch (error) {
            console.error('Failed to load bookings:', error);
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const getBookingStatus = (booking) => {
        const today = new Date();
        const perfDate = new Date(booking.perf_date);
        
        if (perfDate < today) return 'past';
        if (booking.status === 'confirmed') return 'confirmed';
        return 'pending';
    };

    const filterBookingsByTab = (bookings, tab) => {
        const safeBookings = Array.isArray(bookings) ? bookings : [];
        
        // First filter by search query
        let filtered = safeBookings;
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(booking => 
                booking.event_title?.toLowerCase().includes(query) ||
                booking.venue?.toLowerCase().includes(query) ||
                booking.city?.toLowerCase().includes(query) ||
                booking.state?.toLowerCase().includes(query) ||
                booking.artist_name?.toLowerCase().includes(query)
            );
        }
        
        // Then filter by tab and apply appropriate sorting
        let result = [];
        switch(tab) {
            case 'confirmed':
                result = filtered.filter(booking => getBookingStatus(booking) === 'confirmed');
                // Sort confirmed shows: upcoming shows first (ascending date order)
                result.sort((a, b) => new Date(a.perf_date) - new Date(b.perf_date));
                break;
            case 'pending':
                result = filtered.filter(booking => getBookingStatus(booking) === 'pending');
                // Sort pending shows: upcoming shows first (ascending date order)
                result.sort((a, b) => new Date(a.perf_date) - new Date(b.perf_date));
                break;
            case 'past':
                result = filtered.filter(booking => getBookingStatus(booking) === 'past');
                // Sort past shows: most recent past show first (descending date order)
                result.sort((a, b) => new Date(b.perf_date) - new Date(a.perf_date));
                break;
            case 'imported':
                result = filtered; // All imported data - keep original order
                break;
            default:
                result = filtered;
                break;
        }
        
        return result;
    };

    const formatDate = (dateString) => {
        if (!dateString) return { month: 'TBD', day: '?' };
        
        try {
            // Handle YYYY-MM-DD format directly to avoid timezone issues
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateString.split('-');
                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const monthName = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
                return { month: monthName, day: parseInt(day) };
            }
            
            // Fallback for other date formats
            const date = new Date(dateString);
            const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
            const day = date.getDate();
            return { month, day };
        } catch {
            const parts = dateString.split(' ');
            if (parts.length >= 2) {
                return { 
                    month: parts[0].substring(0, 3).toUpperCase(), 
                    day: parts[1].replace(',', '') 
                };
            }
            return { month: 'TBD', day: '?' };
        }
    };

    const getStatusBadgeColor = (status) => {
        switch(status) {
            case 'confirmed': return '#34C759';
            case 'pending': return '#FF9500';
            case 'past': return '#8E8E93';
            default: return '#007AFF';
        }
    };

    const formatTicketsSold = (booking) => {
        const sold = booking.tix_sold || booking.current_ticket_count;
        const capacity = booking.sellable_cap;
        
        if (sold && capacity) {
            return `${sold} / ${capacity} tickets`;
        } else if (sold) {
            return `${sold} tickets`;
        } else if (booking.tix_sold_percent) {
            return `${booking.tix_sold_percent}% sold`;
        }
        return null;
    };

    const handleEdit = (booking) => {
        navigate(`/bookings/edit/${booking._id}`);
    };

    const handleSendForApproval = (booking) => {
        sendBookingForApproval(booking, (approvalData) => {
            showModal('Approval Sent', `Booking approval request sent successfully!`, 'success');
        });
    };

    const handleTicketLink = (ticketLink) => {
        if (ticketLink) {
            window.open(ticketLink, '_blank');
        }
    };

    const getCurrentDisplayData = () => {
        return filterBookingsByTab(bookings, activeTab);
    };

    const handleItemSelect = (booking) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(item => item._id === booking._id);
            if (isSelected) {
                return prev.filter(item => item._id !== booking._id);
            } else {
                return [...prev, booking];
            }
        });
    };

    const handleSelectAll = () => {
        const currentData = getCurrentDisplayData();
        const allSelected = currentData.every(booking => selectedItems.some(item => item._id === booking._id));
        
        if (allSelected) {
            // Deselect all current items
            setSelectedItems(prev => prev.filter(item => !currentData.some(booking => booking._id === item._id)));
        } else {
            // Select all current items
            const newSelections = currentData.filter(booking => !selectedItems.some(item => item._id === booking._id));
            setSelectedItems(prev => [...prev, ...newSelections]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;

        setIsDeleting(true);
        const token = localStorage.getItem('dizeeToken');
        let successCount = 0;
        let failCount = 0;

        for (const item of selectedItems) {
            try {
                await deleteDataAPI(`user/deleteBooking/${item._id}`, token);
                successCount++;
            } catch (error) {
                console.error(`Failed to delete booking at ${item.venue}:`, error);
                failCount++;
            }
        }

        if (successCount > 0) {
            const message = `Successfully deleted ${successCount} booking(s)${failCount > 0 ? `, ${failCount} failed` : ''}`;
            showModal('Delete Complete', message, 'success');
            setSelectedItems([]);
            loadBookings(); // Reload data
        } else {
            showModal('Delete Failed', 'Failed to delete selected bookings. Please try again.', 'error');
        }

        setIsDeleting(false);
        setShowDeleteConfirm(false);
    };

    const handleImportOption = (option) => {
        setShowImportDropdown(false);
        switch(option) {
            case 'manual':
                navigate('/bookings/manual-add');
                break;
            case 'csv':
                navigate('/bookings/bulk-import', { state: { selectedFormat: 'CSV' } });
                break;
            case 'excel':
                navigate('/bookings/bulk-import', { state: { selectedFormat: 'Excel' } });
                break;
            case 'bandsintown':
                navigate('/connectors/bandsintown');
                break;
            default:
                break;
        }
    };

    const tabs = [
        { id: 'confirmed', label: 'Confirmed' },
        { id: 'pending', label: 'Pending' },
        { id: 'past', label: 'Past' },
        { id: 'imported', label: 'Imported' }
    ];

    const filteredBookings = filterBookingsByTab(bookings, activeTab);

    if (loading) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center">
                        <div className="text-white text-left py-[50px]">Loading bookings...</div>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Bookings</span>
                        <div className="flex items-center gap-4">
                            {selectedItems.length > 0 && canDeleteContent('bookings') && (
                                <>
                                    <span className="text-white text-[12px] font-normal">
                                        Selected ({selectedItems.length})
                                    </span>
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={isDeleting}
                                        className="text-red-500 text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </>
                            )}
                            
                            {/* Navigation - Only show when no items are selected */}
                            {selectedItems.length === 0 && (
                                <>
                                    <button 
                                        onClick={() => setShowSearch(!showSearch)}
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        Search Bookings
                                    </button>
                                    
                                    {/* Import Dropdown */}
                                    <div className="relative">
                                        <span 
                                            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                            onClick={() => setShowImportDropdown(!showImportDropdown)}
                                        >
                                            Import
                                        </span>
                                        
                                        {showImportDropdown && (
                                            <>
                                                {/* Overlay to close dropdown */}
                                                <div 
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setShowImportDropdown(false)}
                                                />
                                                
                                                {/* Dropdown Menu */}
                                                <div className="absolute left-0 top-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden">
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('manual')}
                                                    >
                                                        Manual Add
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('csv')}
                                                    >
                                                        CSV
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('excel')}
                                                    >
                                                        Excel
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('bandsintown')}
                                                    >
                                                        Bandsintown
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate(-1)}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="w-full mb-6">
                        <div className="flex">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                                        activeTab === tab.id 
                                            ? 'text-white' 
                                            : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                                    }`}
                                >
                                    {tab.label}
                                    {activeTab === tab.id && (
                                        <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#FFFF00]"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search Interface */}
                    {showSearch && (
                        <div className="w-full px-4 mb-6">
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-4 h-4 text-[#FFFF00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <span className="text-white text-[12px] font-normal">Search Bookings</span>
                                </div>
                                
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Enter event, venue, city, or artist..."
                                        className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FFFF00]"
                                    />
                                    
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        {filteredBookings.length === 0 ? (
                            <div className="text-left py-[60px]">
                                <h3 className="text-white text-[12px] font-semibold mb-2">
                                    No {activeTab} bookings found
                                </h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                                    {activeTab === 'imported' 
                                        ? 'Import your booking data to see all your shows here.'
                                        : `No ${activeTab} shows available right now.`
                                    }
                                </p>
                                <button 
                                    onClick={() => navigate('/bookings/bulk-import')}
                                    className="bg-[#FFFF00] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[#FFFF33] transition-colors"
                                >
                                    Import Bookings
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Select All Button */}
                                {filteredBookings.length > 0 && canDeleteContent('bookings') && (
                                    <div className="px-4 mb-4 text-right">
                                        <button
                                            onClick={handleSelectAll}
                                            className="text-[#FFFF00] text-[10px] hover:text-[#FFFF33] transition-colors"
                                        >
                                            {filteredBookings.every(booking => selectedItems.some(item => item._id === booking._id)) ? 
                                                'Deselect All' : 'Select All'
                                            }
                                        </button>
                                    </div>
                                )}
                                
                                <div className="grid gap-4">
                                    {filteredBookings.map((booking, index) => {
                                        const { month, day } = formatDate(booking.perf_date);
                                        const status = getBookingStatus(booking);
                                        const ticketsSold = formatTicketsSold(booking);
                                        const isSelected = selectedItems.some(item => item._id === booking._id);
                                        
                                        return (
                                            <div 
                                                key={booking._id || index}
                                                className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                                                    isSelected 
                                                        ? 'border-[#FFFF00] bg-[rgba(255,255,0,0.1)]' 
                                                        : 'border-[rgba(255,255,255,0.1)] hover:border-[#FFFF00] hover:bg-[rgba(255,255,0,0.05)]'
                                                }`}
                                                onClick={() => canDeleteContent('bookings') ? handleItemSelect(booking) : null}
                                                style={{ cursor: canDeleteContent('bookings') ? 'pointer' : 'default' }}
                                            >
                                            <div className="flex items-center gap-4">
                                                {/* Date Section */}
                                                <div className="flex flex-col items-center min-w-[60px]">
                                                    <span className="text-[#FFFF00] text-[12px] font-normal">{month}</span>
                                                    <span className="text-[#FFFF00] text-[18px] font-normal">{day}</span>
                                                </div>
                                                
                                                {/* Booking Info */}
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <h3 className="text-white text-[12px] font-normal">
                                                            {booking.event_title || booking.venue || 'Unknown Venue'}
                                                        </h3>
                                                        <div className="flex items-center gap-2">
                                                            {booking.ticket_link && !(booking.source === 'bandsintown' || booking.ticket_link?.includes('bandsintown')) && (
                                                                <button
                                                                    onClick={() => handleTicketLink(booking.ticket_link)}
                                                                    className="p-1 hover:bg-[rgba(255,255,255,0.1)] rounded transition-colors"
                                                                    title="Open ticket link"
                                                                >
                                                                    <svg className="w-4 h-4 text-[rgba(255,255,255,0.7)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            <AddToCalendarButton
                                                                eventData={{
                                                                    summary: booking.event_title || booking.venue || 'Show',
                                                                    date: booking.perf_date,
                                                                    time: booking.show_time || booking.time || null,
                                                                    location: `${booking.city_state || booking.city || ''} ${booking.venue || ''}`.trim() || null,
                                                                    description: `${booking.artist || ''}${booking.artist && booking.venue ? ' at ' : ''}${booking.venue || ''}`.trim() || null
                                                                }}
                                                                iconOnly={true}
                                                                compact={true}
                                                            />
                                                            <button
                                                                onClick={() => handleEdit(booking)}
                                                                className="text-[#FFFF00] text-[10px] px-2 py-1 hover:bg-[rgba(255,255,0,0.1)] rounded transition-colors"
                                                            >
                                                                Edit
                                                            </button>
                                                            {canEditApp('bookings') && activeTab === 'pending' && (
                                                                <button
                                                                    onClick={() => handleSendForApproval(booking)}
                                                                    className="text-[#FF6B35] text-[10px] px-2 py-1 hover:bg-[rgba(255,107,53,0.1)] rounded transition-colors"
                                                                >
                                                                    Submit
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Artist and City in Gray */}
                                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                                                        {booking.artist && booking.city_state 
                                                            ? `${booking.artist} • ${booking.city_state}${booking.show_time ? ` • ${booking.show_time}` : ''}`
                                                            : booking.artist || booking.city_state || 'Artist • Location TBD'
                                                        }
                                                        {booking.show_time && !booking.artist && !booking.city_state && (
                                                            <span className="text-[rgba(255,255,255,0.5)] text-[12px]"> • {booking.show_time}</span>
                                                        )}
                                                    </p>

                                                    {/* Data-Driven Tags - NO VENUE TAGS */}
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        {/* Guarantee Tag */}
                                                        {hasFinancialAccess && booking.guarantee && (
                                                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white">
                                                                {booking.guarantee.toString().startsWith('$') ? booking.guarantee : `$${booking.guarantee}`}
                                                            </div>
                                                        )}

                                                        {/* Tickets Sold Tag */}
                                                        {ticketsSold && (
                                                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white">
                                                                {ticketsSold}
                                                            </div>
                                                        )}

                                                        {/* Status Tag (if not in imported tab) */}
                                                        {activeTab === 'imported' && booking.status && (
                                                            <div 
                                                                className="px-2 py-1 rounded text-[10px] font-semibold text-white"
                                                                style={{ backgroundColor: getStatusBadgeColor(booking.status) }}
                                                            >
                                                                {booking.status.toUpperCase()}
                                                            </div>
                                                        )}





                                                        {/* Bandsintown Tag - Text only */}
                                                        {booking.ticket_link && (booking.source === 'bandsintown' || booking.ticket_link?.includes('bandsintown')) && (
                                                            <button
                                                                onClick={() => handleTicketLink(booking.ticket_link)}
                                                                className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white hover:bg-[rgba(255,255,255,0.2)] transition-colors"
                                                                title="Open on Bandsintown"
                                                            >
                                                                Bandsintown
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            </>
                        )}
                    </div>

                    {/* Help Text - Hidden */}
                </div>
            </div>

                        {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 relative">
                            <h3 className="text-white text-[16px] font-normal text-left">Delete Selected Bookings</h3>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                                disabled={isDeleting}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="mb-8">
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                Are you sure you want to delete {selectedItems.length} selected booking(s)? This action cannot be undone.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-4">
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className="flex-1 bg-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-semibold py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Custom Modal */}
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
            
            {/* Onboarding button for Bookings */}
            <OnboardingActionButton
                onClick={async () => {
                    const phase = getCurrentPhase();
                    
                    // Transition to bookings exploring phase if we're in bookings intro
                    if (phase === ONBOARDING_PHASES.BOOKINGS_INTRO) {
                        const data = await checkUserData();
                        transitionToPhase(ONBOARDING_PHASES.BOOKINGS_EXPLORING);
                        
                        // Navigate to home to show dialogue
                        navigate('/', {
                            state: {
                                showOnboardingDialogue: true,
                                phase: ONBOARDING_PHASES.BOOKINGS_EXPLORING,
                                userData: data
                            }
                        });
                    }
                }}
                showInstructions={getCurrentPhase() === ONBOARDING_PHASES.BOOKINGS_ADD_SHOW}
            />

            {/* Approval Modal */}
            <ApprovalModal
                isOpen={approvalModal.isOpen}
                onClose={closeApprovalModal}
                itemData={approvalModal.itemData}
                itemType={approvalModal.itemType}
                onApprovalSent={approvalModal.onApprovalSent}
            />
        </LayoutHeader>
    );
};

export default ViewAll; 